import { px } from '@adsk/alloy-react-helpers';

import {
  AlloyLegacyThemeDesignTokens,
  ButtonGroupDesignTokens,
} from '../types';
import { buildBoxShadow } from './common';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

/**
 * Corner geometry that makes a row of buttons read as a single segmented
 * control: the outer corners of the first and last segment stay rounded and
 * every adjoining corner is squared off. `dividerColor`, when supplied, draws
 * the separator between adjoining segments.
 */
const getSegmentGeometry = (
  radius: string | undefined,
  dividerColor?: string,
) => {
  const divider = dividerColor
    ? {
        borderRightWidth: '1px',
        borderRightStyle: 'solid' as const,
        borderRightColor: dividerColor,
      }
    : undefined;

  return {
    first: {
      borderTopLeftRadius: radius,
      borderBottomLeftRadius: radius,
      borderTopRightRadius: '0',
      borderBottomRightRadius: '0',
      ...divider,
    },
    middle: {
      borderRadius: '0',
      ...divider,
    },
    last: {
      borderTopLeftRadius: '0',
      borderBottomLeftRadius: '0',
      borderTopRightRadius: radius,
      borderBottomRightRadius: radius,
    },
  };
};

export const getButtonGroupTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): ButtonGroupDesignTokens => {
  const borderSubtle = theme.tokens.colors.border.subtle.value;
  const borderInverse = theme.tokens.colors.border.inverse.value;
  const plainGeometry = getSegmentGeometry(px(theme.borderRadius.small));
  const dividedGeometry = getSegmentGeometry(
    px(theme.borderRadius.small),
    borderSubtle,
  );

  return {
    container: {
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: theme.borderRadius.small,
    },
    secondary: {
      transition: theme.transitions.create(['box-shadow', 'border']),
      '&:hover': {
        boxShadow: theme.halos.hover,
      },
      '& > button:first-child': plainGeometry.first,
      '& > button:not(:first-child):not(:last-child)': plainGeometry.middle,
      '& > button:not(:first-child)': undefined,
      '& > button:last-child': plainGeometry.last,
    },
    tertiary: {
      '& > button:first-child': dividedGeometry.first,
      '& > button:not(:first-child):not(:last-child)': dividedGeometry.middle,
      '& > button:last-child': dividedGeometry.last,
      '& > button': {
        zIndex: 1,
      },
      '&:hover': {
        '& > button': {
          borderRightColor: borderInverse,
        },
      },
    },
    tertiaryWithSelection: {
      '& > button': {
        borderRightColor: borderInverse,
      },
    },
    child: {
      hover: {
        boxShadow: 'none',
      },
      unselectedHover: {
        background: theme.tokens.colors.background.neutral.hover.value,
      },
      focus: {
        boxShadow: theme.halos.focus,
        zIndex: 10,
      },
      active: {
        boxShadow: theme.halos.active,
      },
      disabled: {
        hover: {
          background: theme.tokens.colors.background.primary.value,
        },
      },
      secondaryDisabled: {
        borderColor: theme.tokens.colors.border.value,
        color: theme.tokens.colors.text.inactive.value,
        svg: {
          color: theme.tokens.colors.icon.inactive.value,
        },
      },
      tertiaryDisabled: {
        color: theme.tokens.colors.text.inactive.value,
        svg: {
          color: theme.tokens.colors.icon.inactive.value,
        },
      },
    },
  };
};

export const getButtonGroupTokensWeave = (
  tokens: WeaveTokens,
): ButtonGroupDesignTokens => {
  const generic = tokens?.generic;
  const semantic = tokens?.semantic;
  const button = tokens?.component?.button;
  const borderRadius4 = generic?.['border-radius']?.[4];
  const borderSubtle = semantic?.['border-color']?.divider?.light;
  const borderSelected = semantic?.['border-color']?.selected;
  const plainGeometry = getSegmentGeometry(borderRadius4);
  const dividedGeometry = getSegmentGeometry(borderRadius4, borderSubtle);
  const textDisabled = semantic?.['text-color']?.medium?.default;
  const iconDisabled = semantic?.['icon-color']?.medium;

  return {
    container: {
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: borderRadius4,
    },
    secondary: {
      transition: undefined,
      '&:hover': undefined,
      '& > button:first-child': plainGeometry.first,
      '& > button:not(:first-child):not(:last-child)': plainGeometry.middle,
      '& > button:not(:first-child)': {
        marginLeft: '-1px',
      },
      '& > button:last-child': plainGeometry.last,
    },
    tertiary: {
      '& > button:first-child': dividedGeometry.first,
      '& > button:not(:first-child):not(:last-child)': dividedGeometry.middle,
      '& > button:last-child': dividedGeometry.last,
      '& > button': {
        zIndex: 1,
      },
      '&:hover': {
        '& > button': {
          borderRightColor: borderSelected,
        },
      },
    },
    tertiaryWithSelection: {
      '& > button': {
        borderRightColor: borderSelected,
      },
    },
    child: {
      hover: {
        boxShadow: 'none',
      },
      unselectedHover: undefined,
      focus: {
        boxShadow: buildBoxShadow(button, 'focus'),
        zIndex: 10,
      },
      active: {
        boxShadow: buildBoxShadow(button, 'pressed'),
      },
      /**
       * Weave dims disabled buttons with an opacity token applied by `Button`
       * itself, so the group must not restate a hover background here — doing
       * so would strip the background that opacity is meant to fade.
       */
      disabled: {
        hover: {
          background: undefined,
        },
      },
      secondaryDisabled: {
        borderColor: undefined,
        color: textDisabled,
        svg: {
          color: iconDisabled,
        },
      },
      tertiaryDisabled: {
        color: textDisabled,
        svg: {
          color: iconDisabled,
        },
      },
    },
  };
};
